import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaPaperPlane, FaHeadset, FaQuestionCircle, FaTicketAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import { useAuth } from "../auth/AuthContext";
import { getDisplayNameForUI } from "../utils/displayName";
import { ticketService } from "../services/ticketService";
import { Navbar } from "./Navbar";

const QUESTION_TYPES = [
  "Help me find spare part for my car",
  "Order related query",
  "Refund and return related",
  "I want to become seller",
  "I want to become B2B customer",
  "Error or problem with website/app",
  "Other",
];

// Resolve display name (from displayName utils pattern)
function getContactName(user) {
  if (!user) return "";
  const name = (user.name || user.fullName || user.displayName || "").trim();
  if (name) return name;
  const first = (user.firstName || "").trim();
  const last = (user.lastName || "").trim();
  return [first, last].filter(Boolean).join(" ").trim() || "";
}

const Contact = () => {
  const { user, isAuthenticated } = useAuth();
  const [form, setForm] = useState({
    name: "",
    email: "",
    questionType: "",
    comment: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState({});

  // Pre-fill from logged-in user
  useEffect(() => {
    if (user) {
      const name = getContactName(user) || getDisplayNameForUI(user) || "";
      const email = (user.email && String(user.email).trim()) ? user.email : "";
      setForm((prev) => ({
        ...prev,
        name: prev.name || name,
        email: prev.email || email,
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const validate = () => {
    const err = {};
    if (!form.name?.trim()) err.name = "Name is required";
    if (!form.email?.trim()) err.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Enter a valid email";
    if (!form.questionType) err.questionType = "Please select a question type";
    if (!form.comment?.trim()) err.comment = "Please describe your query";
    return err;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.info("Please log in to submit a support ticket.");
      return;
    }
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setTouched({ name: true, email: true, questionType: true, comment: true });
      const first = Object.values(errors)[0];
      toast.error(first);
      return;
    }
    setSubmitting(true);
    try {
      await ticketService.createTicket({
        subject: form.questionType || "Contact form",
        description: form.comment.trim(),
        category: form.questionType,
        priority: "Medium",
      });
      toast.success("Your ticket has been submitted. We'll get back to you soon.");
      setForm((prev) => ({ ...prev, questionType: "", comment: "" }));
      setTouched({});
    } catch (error) {
      toast.error(error.message || "Failed to submit ticket. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const errors = validate();
  const showError = (field) => touched[field] && errors[field];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 md:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
            Help & <span className="text-red-500">Support</span>
          </h1>
          <div className="flex flex-wrap gap-4 text-sm font-medium">
            <Link to="/faq" className="text-red-600 hover:text-red-700 hover:underline flex items-center gap-1.5">
              <FaQuestionCircle /> FAQ
            </Link>
          </div>
          <Navbar />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Left: Contact info cards */}
          <div className="lg:col-span-2 space-y-5">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FaMapMarkerAlt className="text-red-500 flex-shrink-0" />
                Location
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                Smart Parts Online Private Limited<br />
                Unit Nos. 1609 & 1610, 16th floor, Magnum Global Park<br />
                Golf Course Extension Road, Sector - 58<br />
                Gurugram, Haryana, 122011
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <FaPhoneAlt className="text-sky-500 flex-shrink-0" />
                Phone
              </h2>
              <a href="tel:+911141189222" className="text-sky-600 hover:underline text-sm sm:text-base break-all">
                +91 114 1189222
              </a>
              <p className="text-xs text-gray-500 mt-1">Mon–Sat, 10:00–19:00 IST</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
                <FaHeadset className="text-amber-500 flex-shrink-0" />
                Quick links
              </h2>
              <ul className="space-y-2 text-sm">
                <li><Link to="/faq" className="text-sky-600 hover:underline">FAQ</Link></li>
                <li><Link to="/return-policy" className="text-sky-600 hover:underline">Return policy</Link></li>
                <li><Link to="/myorder" className="text-sky-600 hover:underline">Track order</Link></li>
              </ul>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6 md:p-8">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-1 flex items-center gap-2">
                <FaPaperPlane className="text-red-500" />
                Send us a message
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                Describe your query and we’ll get back to you as soon as possible.
              </p>

              {!isAuthenticated && (
                <div className="mb-6 p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
                  <strong>Log in to submit a ticket.</strong>{" "}
                  <Link to="/login" className="text-amber-700 underline font-medium">Sign in</Link>
                  {" "}or{" "}
                  <Link to="/signup" className="text-amber-700 underline font-medium">sign up</Link>
                  {" "}to send your request.
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    onBlur={() => setTouched((p) => ({ ...p, name: true }))}
                    placeholder="Your name"
                    className={`w-full border rounded-lg px-3 sm:px-4 py-2.5 text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 ${
                      showError("name") ? "border-red-400" : "border-gray-300"
                    }`}
                  />
                  {showError("name") && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    onBlur={() => setTouched((p) => ({ ...p, email: true }))}
                    placeholder="your@email.com"
                    className={`w-full border rounded-lg px-3 sm:px-4 py-2.5 text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 ${
                      showError("email") ? "border-red-400" : "border-gray-300"
                    }`}
                  />
                  {showError("email") && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                </div>

                <div>
                  <label htmlFor="contact-type" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Question type <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="contact-type"
                    name="questionType"
                    value={form.questionType}
                    onChange={handleChange}
                    onBlur={() => setTouched((p) => ({ ...p, questionType: true }))}
                    className={`w-full border rounded-lg px-3 sm:px-4 py-2.5 text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 ${
                      showError("questionType") ? "border-red-400" : "border-gray-300"
                    }`}
                  >
                    <option value="">Select type...</option>
                    {QUESTION_TYPES.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  {showError("questionType") && <p className="mt-1 text-xs text-red-600">{errors.questionType}</p>}
                </div>

                <div>
                  <label htmlFor="contact-comment" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="contact-comment"
                    name="comment"
                    value={form.comment}
                    onChange={handleChange}
                    onBlur={() => setTouched((p) => ({ ...p, comment: true }))}
                    placeholder="Describe your query or feedback..."
                    rows={4}
                    className={`w-full border rounded-lg px-3 sm:px-4 py-2.5 text-sm resize-y focus:ring-2 focus:ring-sky-500 focus:border-sky-500 ${
                      showError("comment") ? "border-red-400" : "border-gray-300"
                    }`}
                  />
                  {showError("comment") && <p className="mt-1 text-xs text-red-600">{errors.comment}</p>}
                </div>

                <button
                  type="submit"
                  disabled={submitting || !isAuthenticated}
                  className="w-full sm:w-auto bg-red-500 hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold px-6 py-2.5 rounded-lg text-sm transition-colors flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane className="text-sm" /> Submit ticket
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Footer section */}
        <div className="mt-10 pt-8 border-t border-gray-200">
          <p className="text-sm text-gray-600 mb-6 max-w-3xl">
            We’re committed to providing clear support. If you have questions, concerns, or feedback, reach out using the form or details above.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Customer support</h3>
              <ul className="space-y-1 text-gray-600">
                <li>
                  <strong>Phone (IVR):</strong>{" "}
                  <a href="tel:+911141189222" className="text-red-600 hover:underline">+91 114 1189222</a>
                </li>
                <li><strong>Hours:</strong> All days, 10:00–19:00 IST (except holidays)</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Business inquiries</h3>
              <p className="text-gray-600">
                For partnerships or bulk orders, see our{" "}
                <a href="/pages/static/become_a_vendor" className="text-sky-600 hover:underline">Become a vendor</a> page.
              </p>
            </div>
          </div>
          <p className="mt-4 text-xs text-amber-700 font-medium">
            Please do not send order returns to our office address; they will not be accepted.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Contact;
